import React from 'react'
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { useGetMembershipReportQuery } from '../../redux/services/reportService'
import AdminLoadingState from '../../components/admin/AdminLoadingState'
import AdminErrorState from '../../components/admin/AdminErrorState'

interface MappedReportData {
  headers: string[][]
  rows: (string | number | undefined | null)[][]
}

export default function Reports() {
  const mappedResponseWithHeaders = React.useRef<MappedReportData | null>(null)
  const {
    data: membershipData,
    isLoading,
    isError
  } = useGetMembershipReportQuery()

  React.useEffect(() => {
    const headers = [
      [
        'id',
        'centerName',
        'streetAddress',
        'city',
        'state',
        'zip',
        'latitude',
        'longitude',
        'centerType',
        'websiteAddress',
        'profile',
        'director',
        'coDirector',
        'centerContacts'
      ]
    ]
    const mappedResponse = membershipData?.map((center) => {
      return Object.entries(center).map(([key, value]) => {
        if (key === 'centerContacts' && value && value instanceof Array) {
          return value.join(';')
        }
        return value as string | number | undefined | null
      })
    })
    if (mappedResponse) {
      mappedResponseWithHeaders.current = {
        headers,
        rows: mappedResponse
      }
    }
  }, [membershipData])

  const downloadCSV = () => {
    if (mappedResponseWithHeaders.current) {
      const today = new Date()
      const allRows = [
        ...mappedResponseWithHeaders.current.headers,
        ...mappedResponseWithHeaders.current.rows
      ]
      const csvData = allRows.map((row) => row.join(',')).join('\n')
      const blob = new Blob([csvData], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.setAttribute('hidden', '')
      a.setAttribute('href', url)
      a.setAttribute(
        'download',
        today.toISOString().split('T')[0] + '_membershipReport.csv'
      )
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  if (isLoading) {
    return <AdminLoadingState type="table" />
  }

  if (isError) {
    return (
      <AdminErrorState message="Error loading reports" showBackButton={false} />
    )
  }

  return (
    <div className="bg-[#F5F5F5] min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-medium text-[#3C2416] mb-8">
          Generate Reports
        </h1>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <div className="bg-white overflow-hidden shadow rounded-lg hover:shadow-lg transition-shadow">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClipboardDocumentListIcon
                    className="h-12 w-12 text-[#AF8A48]"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-base font-medium text-gray-500 truncate">
                      Membership Report
                    </dt>
                    <dd>
                      <div className="text-sm text-gray-900 mt-1">
                        Generate a CSV report that lists all member centers and
                        their contacts.
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4">
              <button
                className="text-sm font-medium text-[#A6410A] hover:text-[#894d35] transition-colors"
                onClick={downloadCSV}
                disabled={!membershipData || membershipData.length === 0}
              >
                Generate Report
              </button>
            </div>
          </div>
        </div>

        {membershipData && membershipData.length === 0 && (
          <div className="mt-8 bg-white rounded-lg p-6 text-center">
            <p className="text-gray-500">
              No membership data available to generate reports.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
